import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import {
  ManagerProgressionTaskSlug,
  ManagerProgressionTaskState,
} from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { glossary } from '@sorare/core/src/lib/glossary';

import { ClaimedTask } from '#src/components/missions/ClaimedTask/index.tsx';
import { useTaskButtonProps } from '#src/components/missions/useTaskButtonProps.tsx';

import type {
  ClaimDailyActionSubTaskMutation,
  ClaimDailyActionSubTaskMutationVariables,
  DailyActionSubTask_taskInterface,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  display: flex;
  padding: var(--intermediate-unit);
  border: 1px solid var(--c-nd-150);
  &.clickable {
    cursor: pointer;
  }
  border-radius: var(--unit);
  position: relative;
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.5) 30.87%,
    rgba(0, 0, 0, 0.29) 68.73%,
    rgba(0, 0, 0, 0) 96.68%
  );

  &.isClaimable {
    cursor: default;
    border-color: var(--c-blue-400);
    box-shadow: inset 0 0 var(--unit) var(--c-blue-400);
  }
`;

const StyledNeonEdge = styled(NeonEdge)`
  width: 70%;
  height: 50%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: auto;
  transform: rotateZ(180deg);
`;
const StyledClaimedTask = styled(ClaimedTask)`
  border-radius: var(--unit);
`;

const Description = styled(LabelL)`
  text-align: left;
`;

const CLAIM_DAILY_ACTION_SUB_TASK_MUTATION = gql`
  mutation ClaimDailyActionSubTaskMutation($taskId: ID!) {
    claimTask(input: { taskId: $taskId }) {
      task {
        id
        aasmState
        parentTask {
          id
          ... on DailyActionTask {
            id
            aasmState
            liveScore
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  ClaimDailyActionSubTaskMutation,
  ClaimDailyActionSubTaskMutationVariables
>;

const ClaimButton = ({ task }: { task: DailyActionSubTask_taskInterface }) => {
  const [claimDailyActionSubTask, { loading: loadingClaimDailyActionSubTask }] =
    useMutation(CLAIM_DAILY_ACTION_SUB_TASK_MUTATION, {
      showErrorsWithSnackNotification: true,
    });
  return (
    <LoadingButton
      loading={loadingClaimDailyActionSubTask}
      size="compact"
      color="primary"
      onClick={async () =>
        claimDailyActionSubTask({ variables: { taskId: task.id } })
      }
    >
      <FormattedMessage {...glossary.claim} />
    </LoadingButton>
  );
};

type Props = {
  task: DailyActionSubTask_taskInterface;
  onDropChallengeClick: Nullable<() => void>;
  onDecisivePlayerPickerClick: Nullable<() => void>;
};

export const DailyActionSubTask = ({
  task,
  onDropChallengeClick,
  onDecisivePlayerPickerClick,
}: Props) => {
  const { to } = useTaskButtonProps({
    task,
  });

  const { description, target, aasmState, name } = task;
  const isClaimable = aasmState === ManagerProgressionTaskState.COMPLETED;

  if (aasmState === ManagerProgressionTaskState.CLAIMED) {
    return <StyledClaimedTask claimedTask={task} />;
  }

  const getExtraProps = () => {
    if (isClaimable) {
      return null;
    }
    if (
      name === ManagerProgressionTaskSlug.COMPLETE_TASK &&
      onDropChallengeClick
    ) {
      return {
        as: ButtonBase,
        onClick: onDropChallengeClick,
      };
    }
    if (
      name === ManagerProgressionTaskSlug.UPSERT_TASK_APPEARANCES &&
      onDecisivePlayerPickerClick
    ) {
      return {
        as: ButtonBase,
        onClick: onDecisivePlayerPickerClick,
      };
    }
    if (aasmState === ManagerProgressionTaskState.READY && !!to) {
      return {
        as: Link,
        to,
      };
    }
    return null;
  };
  const extraProps = getExtraProps();
  const progress = aasmState === ManagerProgressionTaskState.READY ? 0 : 1;
  return (
    <Root
      className={classNames({ isClaimable, clickable: !!to })}
      {...(extraProps || {})}
    >
      {!!isClaimable && <StyledNeonEdge color="var(--c-blue-400)" />}
      <SBHorizontal>
        <Description bold>{description}</Description>
        {isClaimable ? (
          <ClaimButton task={task} />
        ) : (
          !!extraProps && <ChevronRightBold />
        )}
      </SBHorizontal>
      <SBHorizontal>
        <Progress
          value={progress}
          max={target}
          color="linear-gradient(to right, #FFB800 0%, #F0CE1D 37.72%, #FCFF63 100%)"
        />
        <Horizontal gap={0}>
          <LabelS bold>{progress}</LabelS>
          <LabelS>&nbsp;/&nbsp;{target}</LabelS>
        </Horizontal>
      </SBHorizontal>
    </Root>
  );
};

DailyActionSubTask.fragments = {
  taskInterface: gql`
    fragment DailyActionSubTask_taskInterface on TaskInterface {
      id
      name
      description
      aasmState
      target
      ...useTaskButtonProps_taskInterface
      ...ClaimedTask_taskInterface
    }
    ${useTaskButtonProps.fragments.taskInterface}
    ${ClaimedTask.fragments.taskInterface}
  ` as TypedDocumentNode<DailyActionSubTask_taskInterface>,
};
